import _ from 'lodash';
import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import { CardData, StoryStep } from 'types/story';
import { parseURL } from 'common/utils/parse-url';
import { clientLog } from 'common/utils/helpers';
import { location } from 'common/utils/url-helper';
import { IS_DEVEL } from 'common/utils/environment';
import { StoryFacade } from 'common/utils/facades/story-facade';
import CardTransition from 'client/components/common/CardTransition';
import { TRAVERSE_TREE_TARGETS } from 'client/components/common/BuildingBlocks/utils/common';
import { Card, CardEditable } from './Card';
import RedirectController from './RedirectController';
import CustomTracker from './CustomTracker';
import GooglePublisherTagTracker from './GooglePublisherTagTracker';

const { isPreview, isScreenshot } = location.client;
const log = clientLog.extend('CardsManager');

function setHtmlAttrs(lang) {
	const rtlLangs = ['HE'];
	document.documentElement.setAttribute('dir', rtlLangs.includes(lang) ? 'rtl' : 'ltr');
	document.documentElement.setAttribute('lang', lang.toLowerCase());
}

type State = {
	step: StoryStep | null;
	card: CardData | null;
};

type Props = {
	story: StoryFacade;
	isEditor: boolean;
};

/**
 * 1. parse url
 * 2. get first card (url params card or 1st cards in first card in non empty step);
 * 3. render card
 */
class CardsManager extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		setHtmlAttrs(props.story.language);

		this.state = {
			step: null,
			card: null,
		};
	}

	static getDerivedStateFromProps(props, state) {
		const { cardId } = parseURL(window.location.search).searchObject;
		const isInitialRender = !state.card || !state.step;
		const isCardChanged = cardId !== _.get(state, 'card._id');

		if (isInitialRender || isCardChanged) {
			const { story: storyFacade } = props;
			const { card, step } = cardId ? storyFacade.getDataByCardId(cardId) : storyFacade.dataByFirstCard;

			return {
				card,
				step,
			};
		}

		return null;
	}

	get nextDefaultCardId(): string {
		const nextDefaultCard = this.state.step ? this.props.story.getNextDefaultCard(this.state.step) : undefined;
		return nextDefaultCard?._id ?? '';
	}

	render() {
		const { card } = this.state;
		const { isEditor } = this.props;

		if (!card) {
			return null;
		}

		log(`render » ${isEditor ? 'EditorCard' : 'Card'}`, this.state);

		const isPublishedView = this.props.story.isPublished && !isScreenshot() && !isPreview;
		const customTrackerUrl = this.props.story.settings.integrations?.customTracker?.customTrackerUrl;
		const googlePublisherTag = this.props.story.settings.integrations?.googlePublisherTag;
		const isShowGooglePublisherTag = IS_DEVEL ? /* show in preview to debug */ !isEditor : isPublishedView;
		const hasGooglePublisherTagPath =
			googlePublisherTag?.large?.path ||
			googlePublisherTag?.small?.path ||
			googlePublisherTag?.ios?.path ||
			googlePublisherTag?.android?.path;

		return (
			<div id="cards">
				{isEditor ? (
					<CardEditable data={card} story={this.props.story} />
				) : (
					<>
						<RedirectController>
							<CardTransition data={card}>
								<Card
									card={card}
									nextDefaultCardId={this.nextDefaultCardId}
									story={this.props.story}
									treeOptions={{
										target: TRAVERSE_TREE_TARGETS.DEFAULT,
										isCardTree: true,
									}}
								/>
							</CardTransition>
						</RedirectController>
					</>
				)}

				{isPublishedView && customTrackerUrl && (
					<CustomTracker
						storyName={this.props.story.name}
						cardName={card.name}
						trackerUrl={customTrackerUrl}
					/>
				)}

				{isShowGooglePublisherTag && hasGooglePublisherTagPath
					? createPortal(
							// eslint-disable-next-line react/jsx-indent
							<GooglePublisherTagTracker
								key={card._id}
								cardId={card._id}
								settings={googlePublisherTag}
								lang={this.props.story.language}
							/>,
							document.body
						)
					: null}
			</div>
		);
	}
}

export default CardsManager;
